import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../app/store';

/**
 * What the consumer skin asks that today's flow has no place for. Everything
 * else (address, car, basket, time, contact, payment choice, discounts) is
 * the shared booking state the fleet skin reads too, so switching skins keeps
 * it all. Persisted with the booking (app/localStorage, sessionStorage).
 */

export type Powertrain =
  | 'GAS' | 'DIESEL' | 'HYBRID' | 'PHEV' | 'ELECTRIC' | 'UNKNOWN';

export interface EngineOption {
  vehicleId: number;
  engine: string;
  label: string;
  oilVolume?: number | null;
  powertrain: Powertrain;
  planPriceCents?: number | null;
  priceGroup: number;
  /**
   * Set on the portal (engineLabel.ts markSameLabels) when another option
   * shares this label: told apart by its oil capacity, else its engine text.
   */
  sameLabel?: 'oil' | 'engine' | null;
}

/** A car picked from the search box (server searchVehicles VehicleMatch). */
export interface ConsumerVehicle {
  key: string;
  year: number;
  make: string;
  model: string;
  modelName: string;
  displayName: string;
  powertrain: Powertrain;
  confidence: number;
  vehicleId: number;
  engineChoiceNeeded: boolean;
  engines: EngineOption[];
  higherPriceEngine?: EngineOption | null;
}

export type EngineStatus = 'KNOWN' | 'TBC';
export type UnverifiedKind = 'TYPED' | 'DESCRIBED';
export type ConsumerMode = 'plan' | 'visit';
export type ConsumerBilling = 'yearly' | 'monthly';
export interface ConsumerState {
  /** Unit or buzzer, optional. */
  unit: string;
  /** What was typed in the car box. */
  carQuery: string;
  /** The matched car, when one was picked. */
  vehicle: ConsumerVehicle | null;
  /** "Use as typed" / "Describe it": the text, saved exactly as given. */
  unverifiedText: string;
  unverifiedKind: UnverifiedKind | null;
  /** The catalogue row an unverified car is priced and booked with. */
  unverifiedVehicleId: number | null;
  /**
   * KNOWN, or TBC when the customer said "Not sure" or left the engine
   * question unanswered (booked as the base engine, confirmed at the visit).
   */
  engineStatus: EngineStatus;
  /** The engine option picked (a vehicleId), 'unsure', or null. */
  engineChoice: number | 'unsure' | null;
  mode: ConsumerMode;
  billing: ConsumerBilling;
  allInclusive: boolean;
  /** Bookings made on this visit, to offer fleet booking at the 3rd car. */
  carsBooked: number;
}

export const initialConsumerState: ConsumerState = {
  unit: '',
  carQuery: '',
  vehicle: null,
  unverifiedText: '',
  unverifiedKind: null,
  unverifiedVehicleId: null,
  engineStatus: 'KNOWN',
  engineChoice: null,
  // One-off first and the default (owner, 2026-10-27): the plan sits under
  // it as the way to save.
  mode: 'visit',
  billing: 'yearly',
  allInclusive: false,
  carsBooked: 0,
};

export const consumerSlice = createSlice({
  name: 'consumer',
  initialState: initialConsumerState,
  reducers: {
    setUnit: (state, action: PayloadAction<string>) => ({
      ...state, unit: action.payload.slice(0, 40),
    }),
    setCarQuery: (state, action: PayloadAction<string>) => ({
      ...state, carQuery: action.payload,
    }),
    /**
     * A car from the list. Its engine question starts unanswered, which
     * books the base engine (the match's own row) as "to be confirmed".
     */
    pickVehicle: (state, action: PayloadAction<ConsumerVehicle>) => ({
      ...state,
      vehicle: action.payload,
      unverifiedText: '',
      unverifiedKind: null,
      engineStatus: (action.payload.engineChoiceNeeded
        ? 'TBC' : 'KNOWN') as EngineStatus,
      engineChoice: action.payload.engineChoiceNeeded
        ? null : action.payload.vehicleId,
    }),
    /** "Use as typed" or "Describe it". */
    setUnverified: (
      state,
      action: PayloadAction<{
        text: string; kind: UnverifiedKind; vehicleId: number | null;
      }>,
    ) => ({
      ...state,
      vehicle: null,
      unverifiedText: action.payload.text.slice(0, 200),
      unverifiedKind: action.payload.kind,
      unverifiedVehicleId: action.payload.vehicleId,
      engineStatus: 'TBC' as EngineStatus,
      engineChoice: null,
    }),
    chooseEngine: (state, action: PayloadAction<number | 'unsure'>) => ({
      ...state,
      engineChoice: action.payload,
      engineStatus: (action.payload === 'unsure'
        ? 'TBC' : 'KNOWN') as EngineStatus,
    }),
    clearCar: (state) => ({
      ...state,
      vehicle: null,
      unverifiedText: '',
      unverifiedKind: null,
      engineStatus: 'KNOWN' as EngineStatus,
      engineChoice: null,
    }),
    setMode: (state, action: PayloadAction<ConsumerMode>) => ({
      ...state, mode: action.payload,
    }),
    setBilling: (state, action: PayloadAction<ConsumerBilling>) => ({
      ...state, billing: action.payload,
    }),
    setAllInclusive: (state, action: PayloadAction<boolean>) => ({
      ...state, allInclusive: action.payload,
    }),
    /** A booking was made: the next one starts with a fresh car. */
    consumerBooked: (state) => ({
      ...initialConsumerState,
      // Same address, so the same unit / buzzer.
      unit: state.unit,
      carsBooked: state.carsBooked + 1,
    }),
  },
});

export const {
  setUnit,
  setCarQuery,
  pickVehicle,
  setUnverified,
  chooseEngine,
  clearCar,
  setMode,
  setBilling,
  setAllInclusive,
  consumerBooked,
} = consumerSlice.actions;

export const selectConsumer = (state: RootState): ConsumerState => (
  state.consumer ?? initialConsumerState
);

/**
 * Owner rule (2026-10-25): an engine the customer did not pick ("Not sure",
 * or the question left unanswered) on a car whose engines differ in price
 * is booked as the BASE engine: the match's own row (`vehicle.vehicleId`),
 * the server's cheapest option for the year (vehicle-search engineOptions,
 * the same row the public quote prices as "the lower one"). The quote and
 * the booking both price that row, so what is shown is what is charged;
 * the tech confirms the engine at the visit. No tick is asked.
 */
export const engineUnconfirmed = (c: ConsumerState): boolean => (
  !!c.vehicle?.engineChoiceNeeded && typeof c.engineChoice !== 'number'
);

/** The car step is done: a car (its engine defaults to the base), or text. */
export const carAnswered = (c: ConsumerState): boolean => (
  !!c.vehicle || (!!c.unverifiedText.trim() && !!c.unverifiedKind)
);

/** The catalogue row the booking is priced and booked with. */
export const consumerVehicleId = (c: ConsumerState): number | null => {
  if (c.vehicle) {
    return typeof c.engineChoice === 'number'
      ? c.engineChoice
      : c.vehicle.vehicleId;
  }
  return c.unverifiedText ? c.unverifiedVehicleId : null;
};

export default consumerSlice.reducer;
